import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CheckCircle, Loader2, Plus, Trash2, DollarSign } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const blankItem = () => ({
  lender_name: '',
  type_of_debt: '',
  original_amount: null,
  current_balance: null,
  interest_rate: null,
  monthly_payment: null,
  open_date: '',
  maturity_date: '',
  secured_or_unsecured: '',
  current_or_delinquent: '',
  type_of_collateral: '',
});

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const DebtSchedulePublicForm = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([blankItem()]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/debt-schedule-link/${token}`)
      .then(res => {
        setInfo(res.data);
        const existing = res.data?.existing_debt_schedule || [];
        if (existing.length > 0) setItems(existing);
        setLoading(false);
      })
      .catch(() => { setError('This link is invalid or has expired.'); setLoading(false); });
  }, [token]);

  const updateItem = (i, k, v) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems(prev => [...prev, blankItem()]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const totalBalance = items.reduce((s, it) => s + (Number(it.current_balance) || 0), 0);
  const totalMonthly = items.reduce((s, it) => s + (Number(it.monthly_payment) || 0), 0);

  const handleSubmit = async () => {
    // Filter out completely empty rows
    const cleaned = items.filter(it => Object.values(it).some(v => v !== '' && v !== null && v !== undefined));
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/debt-schedule-link/${token}`, { debt_schedule: cleaned });
      setSubmitted(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Thank You!</h2>
          <p className="text-slate-600">Your business debt schedule has been submitted successfully. You may close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="bg-[#081120] rounded-t-2xl px-6 py-5">
          <h1 className="text-xl font-bold text-white">Quidity Direct</h1>
          <p className="text-sm text-slate-300 mt-1">Business Debt Schedule</p>
        </div>
        <div className="bg-white rounded-b-2xl shadow-lg p-6">
          {info?.business_name && (
            <p className="text-sm text-slate-500 mb-4">Application for: <strong className="text-slate-700">{info.business_name}</strong></p>
          )}
          <p className="text-xs text-slate-500 mb-5">Enter each business debt below. Add as many rows as needed. Leave any field blank if it doesn&apos;t apply.</p>

          <div className="space-y-4" data-testid="debt-schedule-public-rows">
            {items.map((item, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                    <DollarSign className="h-3.5 w-3.5 text-blue-600" />
                    Debt #{i + 1}
                  </h3>
                  {items.length > 1 && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={() => removeItem(i)} data-testid={`debt-row-remove-${i}`}>
                      <Trash2 className="h-3 w-3 mr-1" /> Remove
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Lender Name</label>
                    <Input value={item.lender_name || ''} onChange={e => updateItem(i, 'lender_name', e.target.value)} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-lender_name`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Type of Debt</label>
                    <Input value={item.type_of_debt || ''} onChange={e => updateItem(i, 'type_of_debt', e.target.value)} placeholder="e.g., Term Loan, LOC, Equipment" className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-type_of_debt`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Original Amount</label>
                    <Input type="number" value={item.original_amount ?? ''} onChange={e => updateItem(i, 'original_amount', e.target.value === '' ? null : parseFloat(e.target.value))} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-original_amount`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Current Balance</label>
                    <Input type="number" value={item.current_balance ?? ''} onChange={e => updateItem(i, 'current_balance', e.target.value === '' ? null : parseFloat(e.target.value))} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-current_balance`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Interest Rate (%)</label>
                    <Input type="number" step="0.01" value={item.interest_rate ?? ''} onChange={e => updateItem(i, 'interest_rate', e.target.value === '' ? null : parseFloat(e.target.value))} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-interest_rate`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Monthly Payment</label>
                    <Input type="number" value={item.monthly_payment ?? ''} onChange={e => updateItem(i, 'monthly_payment', e.target.value === '' ? null : parseFloat(e.target.value))} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-monthly_payment`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Origin Date</label>
                    <Input type="date" value={item.open_date || ''} onChange={e => updateItem(i, 'open_date', e.target.value)} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-open_date`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Maturity Date</label>
                    <Input type="date" value={item.maturity_date || ''} onChange={e => updateItem(i, 'maturity_date', e.target.value)} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-maturity_date`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Secured / Unsecured</label>
                    <select value={item.secured_or_unsecured || ''} onChange={e => updateItem(i, 'secured_or_unsecured', e.target.value)}
                      className="w-full h-10 px-3 text-sm bg-white text-slate-900 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400" data-testid={`debt-${i}-secured_or_unsecured`}>
                      <option value="">—</option>
                      <option value="Secured">Secured</option>
                      <option value="Unsecured">Unsecured</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Current / Delinquent</label>
                    <select value={item.current_or_delinquent || ''} onChange={e => updateItem(i, 'current_or_delinquent', e.target.value)}
                      className="w-full h-10 px-3 text-sm bg-white text-slate-900 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400" data-testid={`debt-${i}-current_or_delinquent`}>
                      <option value="">—</option>
                      <option value="Current">Current</option>
                      <option value="Delinquent">Delinquent</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 lg:col-span-1">
                    <label className="text-xs text-slate-600 block mb-1">Type of Collateral</label>
                    <Input value={item.type_of_collateral || ''} onChange={e => updateItem(i, 'type_of_collateral', e.target.value)} className="bg-white text-slate-900 placeholder:text-slate-400" data-testid={`debt-${i}-type_of_collateral`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button variant="outline" onClick={addItem} className="mt-4 h-10 border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 w-full" data-testid="debt-row-add">
            <Plus className="h-4 w-4 mr-1" /> Add Another Debt
          </Button>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl px-4 py-3 flex justify-between items-center">
              <span className="text-xs font-semibold text-blue-700">Total Balance</span>
              <span className="text-sm font-bold text-blue-800">{fmtMoney(totalBalance)}</span>
            </div>
            <div className="bg-emerald-50 rounded-xl px-4 py-3 flex justify-between items-center">
              <span className="text-xs font-semibold text-emerald-700">Total Monthly Payment</span>
              <span className="text-sm font-bold text-emerald-800">{fmtMoney(totalMonthly)}</span>
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full h-11 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="debt-public-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            {submitting ? 'Submitting...' : 'Submit Debt Schedule'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DebtSchedulePublicForm;
